<template>
  <div class="ai-card border rounded px-3 py-2">
    <div class="d-flex align-items-start">
      <font-awesome-icon :icon="['fas', 'robot']" class="text-info mr-2 mt-1"/>
      <div class="flex-fill">
        <b-badge v-if="message.ai_triage === 'u'" variant="danger" class="mr-1">Urgent</b-badge>
        <b-badge v-else-if="message.ai_triage === 'o'" variant="success" class="mr-1">Opportunity</b-badge>
        <span>{{ summary || 'No summary.' }}</span>
      </div>
      <b-link v-if="hasMore" class="small text-nowrap ml-2" @click="open = !open">{{ open ? 'Less' : 'More' }}</b-link>
      <b-link class="small text-muted ml-2" title="Hide AI notes on every email (Show AI notes brings them back)" @click="hide">
        <font-awesome-icon :icon="['fas', 'eye-slash']"/>
      </b-link>
    </div>

    <ul v-if="flags.length" class="small mb-0 mt-1 pl-4">
      <li v-for="(f, i) in shownFlags" :key="i">{{ f }}</li>
    </ul>

    <div v-if="stock.length" class="small mt-2">
      <table v-if="have.length" class="stock-table mb-1">
        <tr v-for="s in have" :key="s.requested">
          <td class="pr-3">{{ s.requested }}<span v-if="s.qty > 1" class="text-muted"> &times;{{ s.qty }}</span></td>
          <td class="pr-3">
            <b-link @click.prevent="global_lookup(s.gli, true)" v-b-tooltip.hover :title="s.item">{{ s.gli }}</b-link>
          </td>
          <td class="pr-3 text-nowrap">{{ s.available }} available</td>
          <td class="text-nowrap">
            <span v-if="s.status === 'priced'" class="font-weight-bold">{{ s.price | money }}</span>
            <b-badge v-else variant="warning">Needs pricing</b-badge>
          </td>
        </tr>
      </table>
      <div v-if="missing.length" class="text-muted">Not in stock: {{ missing.map(s => s.requested).join(', ') }}</div>
      <div v-if="quoteNotes.length" class="text-muted">For the quote: {{ quoteNotes.join('; ') }}</div>

      <!-- the assistant's draft quote: made on a tap, never sent -->
      <div v-if="message.ai_quote" class="mt-1">
        <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" class="text-success mr-1"/>
        Draft quote <b-link @click.prevent="global_lookup(message.ai_quote.gli, true)">{{ message.ai_quote.gli }}</b-link>
        <b-badge v-if="message.ai_quote.missing.length" variant="warning" class="ml-1">
          Needs {{ message.ai_quote.missing.join(', ') }}
        </b-badge>
        <span v-else class="text-muted ml-1">ready - it goes on the reply when you use the AI reply</span>
      </div>
      <b-button v-else-if="message.ai_can_quote" variant="outline-success" size="sm" class="mt-1 py-0" @click="makeQuote">
        <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" class="mr-1"/>Create quote
      </b-button>
    </div>

    <div v-if="records.length" class="small mt-1">
      <span class="text-muted mr-1">Records:</span>
      <span v-for="(r, i) in records" :key="r.gli">
        <b-link @click.prevent="global_lookup(r.gli, true)" v-b-tooltip.hover :title="r.why">{{ r.gli }}</b-link><span
          v-if="r.description" class="text-muted"> {{ r.description }}</span><span v-if="i < records.length - 1">, </span>
      </span>
    </div>

    <template v-if="open">
      <div class="small text-muted mt-1" v-if="context.reason">Why it was flagged: {{ context.reason }}</div>
      <div class="small mt-1" v-for="r in records.filter(r => r.why)" :key="'w' + r.gli">
        <b>{{ r.gli }}</b>: {{ r.why }}
      </div>
    </template>

    <div class="small mt-1" :class="message.ai_response_available ? 'text-info' : 'text-muted'" v-if="status">{{ status }}</div>
  </div>
</template>

<script>
import AiNotes from "@/lib/ai-notes-pref";
const FLAGS_SHOWN = 2

export default {
  name: 'message-ai-card',

  props: {
    message: {type: Object, required: true},
  },

  data() {
    return {open: false}
  },

  methods: {
    // gone on every email on this device; "Show AI notes" above the email brings them back
    hide() {
      AiNotes.set(true)
    },

    // a draft quote from what the email asked for, then open it to price and check
    async makeQuote() {
      const r = await this.asyncAction('/api/message/' + this.message.id + '?task=ai_quote', 'PUT')
      this.$set(this.message, 'ai_context', r.ai_context)
      this.$set(this.message, 'ai_quote', r.ai_quote)
      this.$set(this.message, 'ai_can_quote', false)
      this.toast(r.quote.created ? `Draft quote ${r.quote.gli} made - not sent` : `Quote ${r.quote.gli} was already made`)
      this.global_lookup(r.quote.gli, true)
    },
  },

  computed: {
    context() {
      return this.message.ai_context || {}
    },
    summary() {
      return this.context.summary || this.message.ai_summary
    },
    records() {
      return this.context.records || []
    },
    flags() {
      return this.context.flags || []
    },
    // the purchase-request stock check: what we can sell first, then what we cannot
    stock() {
      return this.context.stock || []
    },
    have() {
      return this.stock.filter(s => s.status === 'priced' || s.status === 'needs_pricing')
    },
    missing() {
      return this.stock.filter(s => s.status !== 'priced' && s.status !== 'needs_pricing')
    },
    quoteNotes() {
      return this.context.quote_notes || []
    },
    shownFlags() {
      return this.open ? this.flags : this.flags.slice(0, FLAGS_SHOWN)
    },
    hasMore() {
      return this.flags.length > FLAGS_SHOWN || !!this.context.reason || this.records.some(r => r.why)
    },
    status() {
      if (this.message.ai_response_available)
        return 'A proposed reply is ready - click Reply to review it.'

      return {
        u: 'The proposed reply was used.',
        x: 'The proposed reply was dismissed.',
        s: 'A newer message in this thread replaced the proposed reply.',
      }[this.message.ai_response_status] || (this.message.ai_context ? 'No reply proposed - a person needs to decide this one.' : '')
    },
  },
}
</script>

<style scoped>
.stock-table td {
  padding-top: 1px;
  padding-bottom: 1px;
  vertical-align: top;
}

.ai-card {
  background: #f3f8fb;
  border-color: #cfe3ee !important;
}
</style>
